import { ImprovedNoise } from 'three/addons/math/ImprovedNoise.js';
import { skyTerrainSample } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import {
  SKY_LEVIATHAN_JOURNEY,
  skyLeviathanJourneyView,
} from '../../../packages/domain/src/105-sky-leviathan-journey.mjs';

/** Fixed WebGL2 volume budget. Density is generated once, shared by every bank,
 * and never fetched from a provider. Steps bound fragment work, not simulation. */
export const SKY_VOLUME_BUDGET = Object.freeze({
  full: 8,
  balanced: 4,
  steps: 24,
  balancedSteps: 12,
  size: 32,
});

/** Absolute active-time poses in world metres. The central flight corridor is
 * physically empty; distant banks fade before their bounded depth loop resets.
 * Missing/nonfinite time returns no pose instead of introducing a private clock. */
export function skyVolumePose(index, time) {
  if (!Number.isFinite(time)) return null;
  const side = index % 2 ? -1 : 1;
  const kind = Math.floor(index / 2);
  const phase = (((time / 48 + index * 0.38196601125) % 1) + 1) % 1;
  const fade = Math.min(1, phase * 8, (1 - phase) * 8);
  const shaft = false;
  return {
    x: side * (kind === 2 ? 46 : 33),
    y: kind === 2 ? 32 : kind === 3 ? 12 : kind === 1 ? 20 : 0,
    z: -150 + phase * 110,
    width: kind === 2 ? 62 : 38,
    height: kind === 2 ? 24 : kind === 3 ? 14 : 8,
    depth: 34,
    opacity: fade * 0.64,
    shaft,
  };
}

/** Original frontier atmosphere: broad lateral banks instead of vertical
 * columns. Low-bank centres and slopes follow the canonical terrain sampler;
 * this is approximate surface-conforming mist, not terrain collision geometry.
 * Active seconds move density gently sideways; travelled metres locate hills. */
export function skyFrontierVolumePose(index, time, travel = 0) {
  if (!Number.isFinite(time) || !Number.isFinite(travel)) return null;
  const side = index % 2 ? -1 : 1;
  const kind = Math.floor(index / 2);
  const phase = (((time / 110 + index * 0.38196601125) % 1) + 1) % 1;
  const fade = Math.min(1, phase * 10, (1 - phase) * 10);
  const width = [46, 72, 80, 50][kind];
  const height = [7, 16, 22, 5][kind];
  const x = side * (width / 2 + 15);
  const z = -155 + phase * 100;
  const terrain = skyTerrainSample(x, z, travel);
  return {
    x,
    z,
    width,
    height,
    y: SKY_WORLD.terrainY + terrain.height + [1.7, 16, 29, 0.8][kind],
    depth: [55, 48, 60, 65][kind],
    opacity: fade * [0.78, 0.9, 0.88, 0.72][kind],
    slopeX: kind === 0 || kind === 3 ? terrain.dx : 0,
    slopeZ: kind === 0 || kind === 3 ? terrain.dz : 0,
    shaft: false,
  };
}

/** Cloud-city banks remain at fixed physical heights as the pilot climbs.
 * Ground travel advects their depth; active time only billows density. Side
 * pairs are staggered with real gaps and fade outside the readable approach.
 * Low sea vapour recedes during ascent; the canonical62m deck gains volume.
 * Clouds are decoration, never collision or an enemy-visibility state change. */
export function skyLeviathanVolumePose(index, time, travel = 0, progress = 0) {
  if (![time, travel, progress].every(Number.isFinite)) return null;
  const kind = Math.floor(index / 2),
    side = index % 2 ? -1 : 1;
  const phase = ((((travel * 0.6 + index * 41.5) % 260) + 260) % 260) / 260;
  const fade = Math.min(1, phase * 9, (1 - phase) * 9);
  const width = [74, 90, 106, 64][kind];
  const climb = Math.max(0, Math.min(1, progress));
  return {
    x: side * (width / 2 + 15),
    y: kind === 3 ? 16 : SKY_LEVIATHAN_JOURNEY.cloudCenterY + [-12, 4, 23][kind],
    z: -285 + phase * 260,
    width,
    height: [30, 38, 42, 14][kind],
    depth: [58, 74, 82, 46][kind],
    opacity: fade * (kind === 3 ? 0.46 * (1 - climb * 0.8) : 0.74 + climb * 0.12),
    shaft: false,
    slopeX: 0,
    slopeZ: 0,
  };
}

// Atmosphere-only presentation follows327, including unfinished preview worlds.
// Orbital vacuum and clean interiors intentionally omit cloud volumes. Fields do
// not own stage identity, scenery, weather hazards or encounter state.
const PREPARED_ATMOSPHERES = Object.freeze({
  'cathedral-of-thunder': {
    haze: '#566d86',
    light: '#a8c6dd',
    opacity: 0.62,
    height: [7, 11, 9, 5],
    lift: [1.7, 5, 10, 1],
  },
  'prism-widow': {
    haze: '#344257',
    light: '#b9cced',
    opacity: 0,
    height: [9, 14, 12, 7],
    lift: [8, 17, 28, 13],
  },
  cinderheart: {
    haze: '#343038',
    light: '#e78645',
    opacity: 0.66,
    height: [6, 10, 8, 5],
    lift: [1, 5, 9, 1.4],
  },
  'mourning-bride': {
    haze: '#536477',
    light: '#abc6d4',
    opacity: 0.64,
    height: [6, 8, 7, 4],
    lift: [1.4, 3, 6, 0.7],
  },
  'iron-orchid': {
    haze: '#557b65',
    light: '#d7e6b4',
    opacity: 0.63,
    height: [7, 13, 10, 6],
    lift: [2, 8, 13, 2.5],
  },
  'hourglass-king': {
    haze: '#87795e',
    light: '#d5bf8d',
    opacity: 0.3,
    height: [4, 7, 5, 3],
    lift: [1, 4, 7, 0.8],
  },
  'scrap-emperor': { opacity: 0 },
  vesper: {
    haze: '#637086',
    light: '#b9c3dd',
    opacity: 0.3,
    height: [5, 8, 10, 3],
    lift: [1, 6, 14, 0.7],
  },
  'twin-executioners': {
    haze: '#79868c',
    light: '#e3dbbc',
    opacity: 0.38,
    height: [8, 12, 14, 5],
    lift: [2, 9, 18, 1],
  },
  'abyssal-angler': {
    haze: '#283b50',
    light: '#8092a5',
    opacity: 0.85,
    height: [16, 28, 22, 14],
    lift: [8, 18, 30, 4],
  },
  'redline-express': {
    haze: '#6b8084',
    light: '#d4ded3',
    opacity: 0.45,
    height: [6, 10, 8, 5],
    lift: [1, 9, 20, 1.5],
  },
  'dragon-sovereign': { opacity: 0 },
  'mother-of-mines': {
    haze: '#716349',
    light: '#c8ad70',
    opacity: 0.2,
    height: [3, 4, 5, 2],
    lift: [0.5, 2, 5, 0.7],
  },
  'pale-surgeon': { opacity: 0 },
  'hollow-moon': { opacity: 0 },
  'ember-parliament': {
    haze: '#3b383b',
    light: '#bb805a',
    opacity: 0.75,
    height: [8, 18, 26, 10],
    lift: [3, 14, 24, 2],
  },
  'deep-anchor': {
    haze: '#4b6c85',
    light: '#b9d1e7',
    opacity: 0.15,
    height: [10, 18, 24, 8],
    lift: [12, 28, 46, 6],
  },
  'helios-unbound': { opacity: 0 },
  'last-reflection': { opacity: 0 },
});
const preparedAtmosphere = (worldId) =>
  Object.hasOwn(PREPARED_ATMOSPHERES, worldId) ? PREPARED_ATMOSPHERES[worldId] : null;

/** Prepared worlds use broad terrain-relative density banks, never light
 * columns. Vacuum/clean interiors, unknown IDs or nonfinite clocks return no pose. These clouds
 * remain outside the canonical steering lane and are purely decorative. */
export function skyPreparedVolumePose(index, time, travel, worldId) {
  const profile = preparedAtmosphere(worldId);
  const pose = skyFrontierVolumePose(index, time, travel);
  if (!profile?.opacity || !pose) return null;
  const kind = Math.floor(index / 2);
  pose.height = profile.height[kind];
  pose.y =
    SKY_WORLD.terrainY + skyTerrainSample(pose.x, pose.z, travel).height + profile.lift[kind];
  pose.opacity *= profile.opacity;
  return pose;
}

function densityTexture(T) {
  const size = SKY_VOLUME_BUDGET.size;
  const data = new Uint8Array(size ** 3);
  const noise = new ImprovedNoise();
  let at = 0;
  for (let z = 0; z < size; z++)
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const broad = noise.noise(x / 9, y / 9, z / 9);
        const detail = noise.noise(x / 3.5 + 13, y / 3.5 + 7, z / 3.5 + 5);
        data[at++] = Math.round(
          Math.max(0, Math.min(1, 0.52 + broad * 0.55 + detail * 0.18)) * 255,
        );
      }
  const texture = new T.Data3DTexture(data, size, size, size);
  texture.format = T.RedFormat;
  texture.minFilter = texture.magFilter = T.LinearFilter;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
  return texture;
}

/** Eight bounded raymarched volumes in one instanced draw. Box depth testing
 * conservatively rejects banks behind opaque geometry; this is not a full-scene
 * volumetric-shadow pass. Frontier banks integrate local light optical depth
 * and directional scattering; other worlds keep their prior atmosphere. No
 * opaque-world shadow map is sampled by these volumes. Final camera masks
 * are shared with the peripheral owner, so decoration cannot erase live targets.
 * Reduced/minimal settings omit this draw; pauses and rewinds sample active time. */
export function createSkyVolumeAtmosphere(T, scene, masks) {
  const texture = densityTexture(T);
  const geometry = new T.BoxGeometry(1, 1, 1);
  const properties = new T.InstancedBufferAttribute(
    new Float32Array(SKY_VOLUME_BUDGET.full * 2),
    2,
  );
  geometry.setAttribute('volumeProperties', properties);
  const slopes = new T.InstancedBufferAttribute(new Float32Array(SKY_VOLUME_BUDGET.full * 2), 2);
  geometry.setAttribute('terrainSlope', slopes);
  const uniforms = {
    densityMap: { value: texture },
    frontier: { value: 0 },
    billow: { value: 0 },
    activeTime: { value: 0 },
    flow: { value: new T.Vector3() },
    eye: { value: new T.Vector3() },
    masks: { value: masks },
    maskCount: { value: 0 },
    steps: { value: SKY_VOLUME_BUDGET.steps },
    haze: { value: new T.Color('#a6bda5') },
    lightColor: { value: new T.Color('#fff0cd') },
  };
  const material = new T.ShaderMaterial({
    glslVersion: T.GLSL3,
    uniforms,
    side: T.BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
    vertexShader: `
      attribute vec2 volumeProperties;
      attribute vec2 terrainSlope;
      varying vec3 vScale;
      varying vec2 vSlope;
      uniform vec3 eye;
      varying vec3 vOrigin;
      varying vec3 vDirection;
      varying vec2 vProperties;
      varying vec4 vClip;
      void main() {
        vec3 scale = vec3(instanceMatrix[0][0], instanceMatrix[1][1], instanceMatrix[2][2]);
        vScale = scale;
        vSlope = terrainSlope;
        vOrigin = (eye - instanceMatrix[3].xyz) / scale;
        vDirection = position - vOrigin;
        vProperties = volumeProperties;
        vClip = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        gl_Position = vClip;
      }`,
    fragmentShader: `
      precision highp sampler3D;
      uniform sampler3D densityMap;
      uniform int steps;
      uniform float frontier;
      uniform float billow;
      uniform float activeTime;
      uniform vec3 flow;
      varying vec3 vScale;
      varying vec2 vSlope;
      uniform vec3 haze;
      uniform vec3 lightColor;
      uniform vec3 masks[${masks.length}];
      uniform int maskCount;
      varying vec3 vOrigin;
      varying vec3 vDirection;
      varying vec2 vProperties;
      varying vec4 vClip;
      out vec4 volumeColor;
      float cloudNoise(vec3 p) {
        // Mirrored coordinates preserve continuous samples beyond the texture
        // edges without changing the shared legacy texture's sampler state.
        return texture(densityMap, 1.0 - abs(mod(p, 2.0) - 1.0)).r;
      }
      float density(vec3 p) {
        if (frontier > 0.5) {
          if (any(greaterThan(abs(p), vec3(0.5)))) return 0.0;
          vec3 q = p;
          q.y -= dot(p.xz * vScale.xz, vSlope) / vScale.y;
          // Slow internal shear changes density lobes without moving vertices,
          // making the cloud-city banks billow while their world heights hold.
          if (billow > 0.5) {
            q.x += sin(q.y * 8.0 + q.z * 4.0 + activeTime * 0.07) * 0.026;
            q.y += sin(q.x * 7.0 - q.z * 5.0 + activeTime * 0.055) * 0.024;
          }
          // Unequal overlapping lobes make an actual three-dimensional cloud
          // silhouette. Higher-frequency erosion cuts scalloped soft edges,
          // instead of a smooth ellipsoid whitening the viewport corners.
          float lobe = 1.0 - length((q - vec3(-0.19, -0.04, -0.06)) / vec3(0.28, 0.31, 0.28));
          lobe = max(lobe, 1.0 - length((q - vec3(0.08, 0.10, 0.04)) / vec3(0.29, 0.28, 0.32)));
          lobe = max(lobe, 1.0 - length((q - vec3(0.27, -0.06, -0.06)) / vec3(0.19, 0.23, 0.25)));
          lobe = max(lobe, 1.0 - length((q - vec3(-0.02, -0.19, 0.17)) / vec3(0.27, 0.21, 0.24)));
          float broad = cloudNoise(q * 2.7 + vec3(0.38, 0.61, 0.47) + flow);
          float detail = cloudNoise(q * 6.3 + vec3(1.17, 0.24, 0.83) - flow * 0.7);
          float erosion = (1.0 - broad) * 0.36 + (1.0 - detail) * 0.16;
          float cloud = smoothstep(-0.035, 0.19, lobe - erosion);
          float boundary = 1.0 - smoothstep(0.43, 0.5, max(abs(p.x), max(abs(p.y), abs(p.z))));
          return cloud * boundary * (0.65 + broad * 0.65);
        }
        float envelope = 1.0 - smoothstep(0.28, 0.5, length(p));
        float cloud = smoothstep(0.31, 0.7, texture(densityMap, p + 0.5).r) * envelope;
        return cloud;
      }
      void main() {
        vec2 screen = vClip.xy / vClip.w;
        // A soft clear centre preserves the pilot, aim and movement corridor.
        float safety = smoothstep(0.44, 0.72, max(abs(screen.x), abs(screen.y) * 0.92));
        for (int i = 0; i < ${masks.length}; i++) {
          if (i >= maskCount) break;
          safety *= smoothstep(masks[i].z, masks[i].z + 0.08, distance(screen, masks[i].xy));
        }
        if (safety < 0.005 || vProperties.x < 0.005) discard;
        vec3 direction = normalize(vDirection);
        vec3 inverseDirection = 1.0 / (sign(direction) * max(abs(direction), vec3(0.00001)) + vec3(0.0000001));
        vec3 a = (-vec3(0.5) - vOrigin) * inverseDirection;
        vec3 b = (vec3(0.5) - vOrigin) * inverseDirection;
        vec3 nearHit = min(a, b), farHit = max(a, b);
        float start = max(0.0, max(nearHit.x, max(nearHit.y, nearHit.z)));
        float end = min(farHit.x, min(farHit.y, farHit.z));
        if (start >= end) discard;
        float stepSize = (end - start) / float(steps);
        // Spatial-only jitter avoids temporal flicker and stays identical on pause.
        float jitter = fract(sin(dot(floor(gl_FragCoord.xy), vec2(12.9898, 78.233))) * 43758.5453);
        vec3 sum = vec3(0.0);
        float alpha = 0.0;
        for (int i = 0; i < ${SKY_VOLUME_BUDGET.steps}; i++) {
          if (i >= steps) break;
          vec3 p = vOrigin + direction * (start + (float(i) + jitter) * stepSize);
          float d = density(p);
          float extinction;
          vec3 color;
          if (frontier > 0.5) {
            vec3 sunDirection = normalize(vec3(-0.55, 0.72, -0.42));
            float lightDepth = 0.0;
            // Integrate to the actual sun-facing box exit. Fixed long steps
            // skipped thin mist entirely and washed every bank equally bright.
            vec3 localSun = sunDirection / vScale;
            vec3 exitDistance = (sign(localSun) * 0.5 - p) / localSun;
            float lightStepLength = max(0.0, min(exitDistance.x, min(exitDistance.y, exitDistance.z))) / 4.0;
            for (int lightStep = 0; lightStep < 4; lightStep++) {
              vec3 lightPoint = p + localSun * (float(lightStep) + 0.5) * lightStepLength;
              lightDepth += density(lightPoint) * lightStepLength * 0.22;
            }
            float cosine = dot(normalize(direction * vScale), sunDirection);
            float g = 0.55;
            float phase = (1.0 - g*g) / pow(max(0.1, 1.0 + g*g - 2.0*g*cosine), 1.5);
            float transmittance = exp(-lightDepth);
            float worldStep = stepSize * length(direction * vScale);
            extinction = 1.0 - exp(-d * worldStep * 0.14);
            // Thin sun-facing edges remain warm; dense cores retain cool depth.
            color = haze * 0.22 + lightColor * transmittance * (0.3 + min(phase, 3.2) * 0.52);
          } else {
            extinction = 1.0 - exp(-d * stepSize * 5.2);
            float sunDensity = density(p + vec3(-0.12, 0.16, 0.08));
            float sunlight = exp(-sunDensity * 2.8);
            color = mix(haze * 0.7, lightColor, 0.3 + sunlight * 0.7);
          }
          sum += (1.0 - alpha) * extinction * color;
          alpha += (1.0 - alpha) * extinction;
          if (alpha > 0.97) break;
        }
        if (alpha < 0.005) discard;
        volumeColor = vec4(sum / max(alpha, 0.001), min(mix(0.48, 0.78, frontier), alpha * vProperties.x) * safety);
        volumeColor = linearToOutputTexel(volumeColor);
      }`,
  });
  const mesh = new T.InstancedMesh(geometry, material, SKY_VOLUME_BUDGET.full);
  const matrix = new T.Matrix4();
  const sunlight = new T.Color('#fff8e8');
  mesh.name = 'sky-volumetric-cloud-light';
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.visible = false;
  mesh.count = 0;
  scene.add(mesh);
  let dead = false;
  return {
    get visible() {
      return mesh.visible;
    },
    draw(state, stage, decoration, quiet) {
      const phase = state.leviathan?.encounter?.phase;
      const prepared = preparedAtmosphere(stage.worldId);
      const whale = stage.encounterId === 'gilded-leviathan';
      const bossAction = state.boss && !state.traversal && state.boss.controlMode !== 'flyby';
      mesh.visible =
        !dead &&
        !quiet &&
        decoration.tier < 2 &&
        prepared?.opacity !== 0 &&
        !bossAction &&
        (!phase || phase === 'arrival') &&
        ['playing', 'paused'].includes(state.status) &&
        Number.isFinite(state.time);
      mesh.count = mesh.visible
        ? decoration.tier === 0
          ? SKY_VOLUME_BUDGET.full
          : SKY_VOLUME_BUDGET.balanced
        : 0;
      if (!mesh.visible) return;
      uniforms.steps.value =
        decoration.tier === 0 ? SKY_VOLUME_BUDGET.steps : SKY_VOLUME_BUDGET.balancedSteps;
      const opening = stage.artVariant === 'first-dragon';
      uniforms.frontier.value = opening || prepared || whale ? 1 : 0;
      uniforms.billow.value = whale ? 1 : 0;
      uniforms.activeTime.value = state.time;
      uniforms.flow.value.set(
        Math.sin(state.time * 0.013) * 0.035,
        0,
        Math.sin(state.time * 0.009) * 0.025,
      );
      uniforms.haze.value.set(stage.fog ?? '#a6bda5');
      uniforms.lightColor.value.set(stage.accent ?? '#fff0cd').lerp(sunlight, 0.7);
      if (opening) {
        uniforms.haze.value.set('#7f9b9b');
        uniforms.lightColor.value.set('#ffe7b8');
      } else if (whale) {
        uniforms.haze.value.set('#a2a8ae');
        uniforms.lightColor.value.set('#ffe4b5');
      } else if (prepared) {
        uniforms.haze.value.set(prepared.haze);
        uniforms.lightColor.value.set(prepared.light);
      }
      for (let i = 0; i < mesh.count; i++) {
        const pose = opening
          ? skyFrontierVolumePose(
              i,
              state.time,
              Number.isFinite(state.terrainTravel) ? state.terrainTravel : 0,
            )
          : whale
            ? skyLeviathanVolumePose(
                i,
                state.time,
                Number.isFinite(state.terrainTravel) ? state.terrainTravel : 0,
                skyLeviathanJourneyView(state).progress,
              )
            : prepared
              ? skyPreparedVolumePose(
                  i,
                  state.time,
                  Number.isFinite(state.terrainTravel) ? state.terrainTravel : 0,
                  stage.worldId,
                )
              : skyVolumePose(i, state.time);
        matrix.makeScale(pose.width, pose.height, pose.depth);
        matrix.setPosition(pose.x, pose.y, pose.z);
        mesh.setMatrixAt(i, matrix);
        properties.setXY(i, pose.opacity, pose.shaft ? 1 : 0);
        slopes.setXY(i, pose.slopeX ?? 0, pose.slopeZ ?? 0);
      }
      mesh.instanceMatrix.needsUpdate = properties.needsUpdate = slopes.needsUpdate = true;
    },
    cull(camera, maskCount, overflow = false) {
      if (overflow) {
        mesh.visible = false;
        mesh.count = 0;
      }
      uniforms.eye.value.setFromMatrixPosition(camera.matrixWorld);
      uniforms.maskCount.value = maskCount;
    },
    dispose() {
      if (dead) return;
      dead = true;
      mesh.visible = false;
      mesh.count = 0;
      mesh.removeFromParent();
      for (const resource of [mesh, geometry, material, texture]) resource.dispose();
    },
  };
}
